import React, { useState, useCallback } from "react";
import { Phone, MapPin, Clock, Send, Check, Instagram, TikTok } from "./Icons";
import { useReveal } from "../hooks/useReveal";
import { CLINICS, getClinic, whatsappUrl, formatHours } from "../data/clinics";

const contactInfoFor = (clinic) => [
  {
    icon: Phone,
    label: "WhatsApp",
    value: clinic.phoneDisplay,
    href: whatsappUrl(clinic),
    external: true,
  },
  {
    icon: MapPin,
    label: "Location",
    value: clinic.address,
    href: clinic.mapsUrl,
    external: true,
  },
  {
    icon: Clock,
    label: "Hours",
    value: formatHours(clinic),
    href: null,
    external: false,
  },
];

const SERVICES_OPTIONS = [
  "Facial Treatment",
  "Anti-Aging Solutions",
  "Acne & Scar Treatment",
  "Face Contouring",
  "Laser Treatment",
  "Body Contouring",
  "Other",
];

export default function ContactForm() {
  const [headerRef, headerVisible] = useReveal();
  const [formRef, formVisible] = useReveal();
  const [infoRef, infoVisible] = useReveal();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  // Drives both the form's "Preferred Clinic" field and the info/map column
  const [clinicId, setClinicId] = useState(CLINICS[0].id);
  const clinic = getClinic(clinicId);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitStatus("loading");

      try {
        const msg = [
          `Halo ${clinic.name}!`,
          "",
          `Nama: ${formData.name}`,
          `Phone: ${formData.phone}`,
          `Email: ${formData.email}`,
          `Clinic: ${clinic.name}`,
          `Service: ${formData.service}`,
          `Message: ${formData.message}`,
        ].join("\n");
        window.open(whatsappUrl(clinic, msg), "_blank");
        setSubmitStatus("success");
        setFormData({ name: "", phone: "", email: "", service: "", message: "" });
      } catch {
        setSubmitStatus("error");
      }

      setTimeout(() => setSubmitStatus(null), 5000);
    },
    [formData, clinic]
  );

  const inputClasses =
    "w-full px-4 py-3 bg-bg border border-border rounded-xl text-sm text-primary placeholder:text-text-light focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all duration-200";

  return (
    <section id="contact" className="section bg-white">
      <div className="container-wide mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className={`reveal ${headerVisible ? "visible" : ""} text-center max-w-2xl mx-auto mb-16`}
        >
          <span className="section-label">Get in Touch</span>
          <h2 className="font-display text-display-lg text-primary mb-4 text-balance">
            Start Your Beauty <span className="text-accent-700">Journey</span>
          </h2>
          <p className="text-text-muted leading-relaxed">
            Ready to transform? Send us a message or visit one of our clinics
            for a personalized consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Form */}
          <div
            ref={formRef}
            className={`reveal ${formVisible ? "visible" : ""} lg:col-span-3`}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-primary mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-primary mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+62 xxx-xxxx-xxxx"
                    required
                    className={inputClasses}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className={inputClasses}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="clinic" className="block text-sm font-medium text-primary mb-2">
                    Preferred Clinic
                  </label>
                  <select
                    id="clinic"
                    name="clinic"
                    value={clinicId}
                    onChange={(e) => setClinicId(e.target.value)}
                    className={`${inputClasses} cursor-pointer`}
                  >
                    {CLINICS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-primary mb-2">
                    Treatment Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className={`${inputClasses} cursor-pointer`}
                  >
                    <option value="">Select a treatment...</option>
                    {SERVICES_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-primary mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your goals or concerns..."
                  rows={4}
                  className={`${inputClasses} resize-none`}
                />
              </div>

              <button
                type="submit"
                disabled={submitStatus === "loading"}
                className="btn-primary btn-lg w-full sm:w-auto disabled:opacity-50"
              >
                {submitStatus === "loading" ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : submitStatus === "success" ? (
                  <>
                    <Check size={16} />
                    Message Sent!
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send via WhatsApp
                  </>
                )}
              </button>

              {submitStatus === "error" && (
                <p className="text-sm text-red-500 mt-2">
                  Something went wrong. Please try again or contact us directly.
                </p>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div
            ref={infoRef}
            className={`reveal ${infoVisible ? "visible" : ""} lg:col-span-2`}
          >
            {/* Clinic switcher */}
            <div
              role="tablist"
              aria-label="Choose clinic"
              className="flex p-1 mb-6 bg-bg border border-border/60 rounded-2xl"
            >
              {CLINICS.map((c) => (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={c.id === clinicId}
                  onClick={() => setClinicId(c.id)}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 cursor-pointer ${
                    c.id === clinicId
                      ? "bg-white text-primary shadow-soft"
                      : "text-text-muted hover:text-primary"
                  }`}
                >
                  {c.area}
                </button>
              ))}
            </div>

            <div className="space-y-6">
              {contactInfoFor(clinic).map((info) => {
                const IconComponent = info.icon;
                const Wrapper = info.href ? "a" : "div";
                const wrapperProps = info.href
                  ? {
                      href: info.href,
                      target: info.external ? "_blank" : undefined,
                      rel: info.external ? "noopener noreferrer" : undefined,
                    }
                  : {};

                return (
                  <Wrapper
                    key={info.label}
                    {...wrapperProps}
                    className={`flex gap-4 p-4 rounded-2xl border border-border/60 transition-all duration-200 ${
                      info.href
                        ? "hover:border-primary/20 hover:bg-bg cursor-pointer"
                        : ""
                    }`}
                  >
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center">
                      <IconComponent size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-text-muted mb-1">
                        {info.label}
                      </div>
                      <div className="text-sm text-primary font-medium leading-relaxed">
                        {info.value}
                      </div>
                    </div>
                  </Wrapper>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.instagram.com/marviebeauty_by_dr.winayani"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-2xl border border-border/60 hover:border-primary/20 hover:bg-bg transition-all duration-200 cursor-pointer flex-1"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center">
                  <Instagram size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-xs font-medium text-text-muted mb-1">Instagram</div>
                  <div className="text-sm text-primary font-medium">@marviebeauty</div>
                </div>
              </a>
              <a
                href="https://www.tiktok.com/@marviebeautybydrwinayani"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-2xl border border-border/60 hover:border-primary/20 hover:bg-bg transition-all duration-200 cursor-pointer flex-1"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center">
                  <TikTok size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-xs font-medium text-text-muted mb-1">TikTok</div>
                  <div className="text-sm text-primary font-medium">@marviebeauty</div>
                </div>
              </a>
            </div>

            {/* Map for the selected clinic */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-border/60 aspect-[4/3]">
              {clinic.mapEmbedUrl ? (
                <iframe
                  key={clinic.id}
                  title={`${clinic.name} location`}
                  src={clinic.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <a
                  href={clinic.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center gap-3 bg-bg text-center px-6
                             hover:bg-accent-50 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center">
                    <MapPin size={22} className="text-primary" />
                  </div>
                  <span className="text-sm font-medium text-primary">Open {clinic.name} in Google Maps</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
